import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmt, fmtDateShort } from '../lib/format';
import { Trash, MagnifyingGlass, Receipt, Storefront, Globe } from '@phosphor-icons/react';

const SCOPES = [
  { v: 'today', label: '今日' },
  { v: 'week', label: '本周' },
  { v: 'month', label: '本月' },
  { v: 'all', label: '全部' },
  { v: 'custom', label: '自定义' },
];

const TYPES = [
  { v: 'all', label: '全部' },
  { v: 'offline', label: '⛺ 线下' },
  { v: 'online', label: '🌐 线上' },
];

const PAYMENT_FILTERS = [
  { v: 'all', label: '全部' },
  { v: 'paid', label: '✅ 已收款' },
  { v: 'owed', label: '🔴 待收款' },
];

export default function Orders() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const owedMode = searchParams.get('owed') === '1';
  const [scope, setScope] = useState(owedMode ? 'all' : 'today');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState(owedMode ? 'owed' : 'all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [confirmingPaid, setConfirmingPaid] = useState(null);

  // Custom date range
  const todayStr = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (typeFilter !== 'all') params.order_type = typeFilter;
      if (paymentFilter !== 'all') params.payment_status = paymentFilter;
      if (scope === 'custom') {
        params.start = new Date(startDate + 'T00:00:00Z').toISOString();
        params.end = new Date(endDate + 'T23:59:59Z').toISOString();
      }
      const r = await api.get('/orders', { params });
      setOrders(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (scope !== 'custom') load();
  }, [scope, typeFilter, paymentFilter]);

  // For custom scope, load initially when switching to it
  useEffect(() => {
    if (scope === 'custom') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/orders/${deleting.id}`, { data: { reason: deleting.reason || '' } });
      setDeleting(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || '删除失败');
    }
  };

  const markPaid = async (id, partialAmount, notes) => {
    try {
      const body = {};
      if (partialAmount != null && partialAmount !== '') body.amount = parseFloat(partialAmount);
      if (notes) body.notes = notes;
      await api.put(`/orders/${id}/mark-paid`, body);
      setConfirmingPaid(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || '操作失败');
    }
  };

  const filtered = orders.filter((o) =>
    !query || o.customer_email.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-5 fade-in" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">订单列表</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {isAdmin ? '查看全部订单' : '仅显示我录的订单'}
          </p>
        </div>
        <Link to="/new-order" className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2 self-start" data-testid="new-order-link">
          + 新订单
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
        {/* Scope */}
        <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-800 p-1 flex-wrap">
          {SCOPES.map((s) => (
            <button
              key={s.v}
              data-testid={`orders-scope-${s.v}`}
              onClick={() => setScope(s.v)}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                scope === s.v ? 'bg-indigo-600 text-white font-medium' : 'text-slate-400 hover:text-white'
              }`}
            >{s.label}</button>
          ))}
        </div>

        {/* Type filter */}
        <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-800 p-1">
          {TYPES.map((t) => (
            <button
              key={t.v}
              data-testid={`orders-type-${t.v}`}
              onClick={() => setTypeFilter(t.v)}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                typeFilter === t.v ? 'bg-sky-600 text-white font-medium' : 'text-slate-400 hover:text-white'
              }`}
            >{t.label}</button>
          ))}
        </div>

        {/* Payment status filter */}
        <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-800 p-1">
          {PAYMENT_FILTERS.map((p) => (
            <button
              key={p.v}
              data-testid={`orders-payment-${p.v}`}
              onClick={() => setPaymentFilter(p.v)}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                paymentFilter === p.v ? (p.v === 'owed' ? 'bg-rose-600 text-white font-medium' : 'bg-emerald-600 text-white font-medium') : 'text-slate-400 hover:text-white'
              }`}
            >{p.label}</button>
          ))}
        </div>

        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="搜索客户邮箱…"
            className="input-base pl-10"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="orders-search"
          />
        </div>
      </div>

      {/* Custom date range */}
      {scope === 'custom' && (
        <div className="card-base p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="orders-date-range">
          <span className="text-xs text-slate-400 font-medium">日期范围:</span>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input-base font-num text-sm py-1.5 max-w-[160px]"
            data-testid="orders-start-date"
          />
          <span className="text-slate-500 text-sm">至</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={todayStr}
            onChange={(e) => setEndDate(e.target.value)}
            className="input-base font-num text-sm py-1.5 max-w-[160px]"
            data-testid="orders-end-date"
          />
          <button
            onClick={load}
            className="btn-primary text-sm py-1.5 px-4"
            data-testid="orders-apply-date"
          >应用</button>
        </div>
      )}

      {loading && <div className="card-base p-12 text-center text-slate-500">加载中…</div>}

      {!loading && filtered.length === 0 && (
        <div className="card-base p-12 text-center">
          <Receipt size={40} weight="duotone" className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">没有订单</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="card-base overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1F2B45] text-xs uppercase text-slate-400 font-semibold tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">时间</th>
                  <th className="px-4 py-3 text-left">类型</th>
                  <th className="px-4 py-3 text-left">客户</th>
                  {isAdmin && <th className="px-4 py-3 text-left">员工</th>}
                  <th className="px-4 py-3 text-right">金额</th>
                  <th className="px-4 py-3 text-right">汇率/手续费</th>
                  <th className="px-4 py-3 text-right">Net USDT</th>
                  {isAdmin && <th className="px-4 py-3 text-right">利润 USDT</th>}
                  <th className="px-4 py-3 text-center">付款</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const isOnline = o.order_type === 'online';
                  const isOwed = o.payment_status === 'owed';
                  return (
                  <tr key={o.id} className={`border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors ${isOwed ? 'bg-rose-900/10' : ''}`}>
                    <td className="px-4 py-3 text-sm text-slate-300 font-num whitespace-nowrap">{fmtDateShort(o.created_at)}</td>
                    <td className="px-4 py-3">
                      {isOnline ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
                          <Globe size={11} weight="duotone" /> 线上
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                          <Storefront size={11} weight="duotone" /> 线下
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Link to={`/customers/${encodeURIComponent(o.customer_email)}`}
                            className="text-indigo-300 hover:text-indigo-200 truncate block max-w-[180px]">{o.customer_email}</Link>
                    </td>
                    {isAdmin && <td className="px-4 py-3 text-sm text-slate-400">{o.employee_name}</td>}
                    <td className="px-4 py-3 text-sm text-right font-num text-white">
                      {fmt(o.amount)} <span className="text-xs text-slate-500">{o.currency}</span>
                      {isOnline && o.credited_amount != null && (
                        <div className="text-xs text-slate-500 mt-0.5">到账 {fmt(o.credited_amount)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-num text-slate-400">
                      {isOnline ? <span className="text-slate-600">—</span> : (
                        <>{o.sell_rate ? `${o.sell_rate} / ` : ''}<span className="text-slate-500">{o.fee_percent}%</span></>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-num text-emerald-300">{fmt(o.net_usdt)}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm text-right font-num text-emerald-400 font-semibold">
                        {fmt(o.total_profit_usdt)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      {isOwed ? (
                        <>
                          <button
                            onClick={() => setConfirmingPaid(o)}
                            data-testid={`mark-paid-${o.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-emerald-500/15 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors"
                          >
                            🔴 待收 → ✓
                          </button>
                          {o.paid_amount > 0 && (
                            <div className="text-[10px] text-amber-300 font-num mt-1">
                              已收 {fmt(o.paid_amount)} / 欠 {fmt((o.amount || 0) - (o.paid_amount || 0))}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                          ✓ 已收
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setDeleting({ id: o.id, reason: '' })}
                        data-testid={`delete-order-${o.id}`}
                        className="text-rose-400 hover:text-rose-300 p-1.5 rounded hover:bg-rose-500/10"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-800">
            {filtered.map((o) => {
              const isOnline = o.order_type === 'online';
              const isOwed = o.payment_status === 'owed';
              return (
              <div key={o.id} className={`p-4 ${isOwed ? 'bg-rose-900/10' : ''}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {isOnline ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
                          <Globe size={10} weight="duotone" /> 线上
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                          <Storefront size={10} weight="duotone" /> 线下
                        </span>
                      )}
                      {isOwed ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30 font-medium">
                          🔴 待收款
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                          ✓ 已收
                        </span>
                      )}
                      <Link to={`/customers/${encodeURIComponent(o.customer_email)}`} className="text-sm text-indigo-300 truncate">
                        {o.customer_email}
                      </Link>
                    </div>
                  </div>
                  <button onClick={() => setDeleting({ id: o.id, reason: '' })} className="text-rose-400 ml-2 p-1">
                    <Trash size={14} />
                  </button>
                </div>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-num text-lg text-white">{fmt(o.amount)} <span className="text-xs text-slate-500">{o.currency}</span></span>
                  <span className="font-num text-sm text-emerald-300">
                    {isOnline ? `到账 ${fmt(o.net_usdt)}` : `Net ${fmt(o.net_usdt)} USDT`}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500 font-num">
                  <span>{fmtDateShort(o.created_at)} · {isAdmin && o.employee_name + ' · '}{isOnline ? '线上充值' : (o.sell_rate ? `汇率 ${o.sell_rate} · 费 ${o.fee_percent}%` : `费 ${o.fee_percent}%`)}</span>
                  {isAdmin && <span className="text-emerald-400 font-semibold">+{fmt(o.total_profit_usdt)}</span>}
                </div>
                {isOwed && (
                  <>
                    {o.paid_amount > 0 && (
                      <div className="mt-1.5 text-xs text-amber-300 font-num">
                        已收 {fmt(o.paid_amount)} / 欠 {fmt((o.amount || 0) - (o.paid_amount || 0))} {o.currency}
                      </div>
                    )}
                    <button
                      onClick={() => setConfirmingPaid(o)}
                      data-testid={`mark-paid-mobile-${o.id}`}
                      className="mt-2 w-full text-xs py-1.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-medium hover:bg-emerald-500/25"
                    >
                      ✓ 收款
                    </button>
                  </>
                )}
              </div>
            )})}
          </div>
        </div>
      )}

      {/* Delete dialog */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="card-base p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-2">确认删除订单?</h3>
            <p className="text-sm text-slate-400 mb-4">删除后会记录到日志,管理员可查看。</p>
            <textarea
              placeholder="删除原因(可选)"
              className="input-base h-20 resize-none mb-4"
              value={deleting.reason}
              onChange={(e) => setDeleting({ ...deleting, reason: e.target.value })}
              data-testid="delete-reason"
            />
            <div className="flex gap-3">
              <button onClick={() => setDeleting(null)} className="btn-secondary flex-1">取消</button>
              <button onClick={handleDelete} className="btn-danger flex-1" data-testid="confirm-delete">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Mark paid dialog */}
      {confirmingPaid && (
        <PaymentDialog
          order={confirmingPaid}
          onClose={() => setConfirmingPaid(null)}
          onSubmit={markPaid}
          currentUser={user}
        />
      )}
    </div>
  );
}


function PaymentDialog({ order, onClose, onSubmit, currentUser }) {
  const total = parseFloat(order.amount || 0);
  const paidSoFar = parseFloat(order.paid_amount || 0);
  const remaining = Math.max(total - paidSoFar, 0);
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const amt = parseFloat(amount || 0);
  const willBeFullyPaid = amt >= remaining - 0.01;
  const newRemaining = Math.max(remaining - amt, 0);
  const valid = amt > 0 && amt <= remaining + 0.01;

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(order.id, amount, notes);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card-base p-6 max-w-md w-full" data-testid="payment-dialog">
        <h3 className="text-lg font-semibold text-white mb-1">确认收款</h3>
        <p className="text-xs text-slate-500 mb-4">订单 {order.id?.slice(0, 8)}…</p>

        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">客户</span><span className="text-indigo-300 truncate ml-2">{order.customer_email}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">订单总额</span><span className="text-white font-num">{fmt(total)} {order.currency}</span></div>
          {paidSoFar > 0 && (
            <div className="flex justify-between"><span className="text-slate-400">已收</span><span className="text-emerald-300 font-num">{fmt(paidSoFar)} {order.currency}</span></div>
          )}
          <div className="flex justify-between border-t border-slate-800 pt-1.5 mt-1.5">
            <span className="text-slate-300 font-semibold">剩余欠款</span>
            <span className="text-rose-300 font-num font-semibold">{fmt(remaining)} {order.currency}</span>
          </div>
        </div>

        {order.payments && order.payments.length > 0 && (
          <div className="mb-4">
            <div className="label-tiny mb-1.5">收款历史 ({order.payments.length})</div>
            <div className="space-y-1">
              {order.payments.map((p) => (
                <div key={`${p.paid_at}-${p.amount}`} className="text-xs text-slate-400 flex justify-between bg-slate-900/40 rounded px-2 py-1.5">
                  <span>{fmtDateShort(p.paid_at)} · {p.paid_by} {p.notes ? `· ${p.notes}` : ''}</span>
                  <span className="font-num text-emerald-300">+{fmt(p.amount)} {order.currency}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 mb-4">
          <div>
            <label className="label-tiny block mb-1">本次收款金额 ({order.currency})</label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                max={remaining}
                className="input-base font-num text-lg flex-1"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                data-testid="payment-amount"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setAmount(remaining.toFixed(2))}
                className="btn-secondary text-xs px-3"
                data-testid="payment-full"
              >全额</button>
            </div>
            <div className={`text-xs mt-1.5 font-num ${willBeFullyPaid ? 'text-emerald-400' : 'text-amber-300'}`}>
              {willBeFullyPaid
                ? `✓ 本次收 ${fmt(amt)} → 订单完成`
                : `⚠️ 本次收 ${fmt(amt)} → 还剩 ${fmt(newRemaining)} ${order.currency} 待收`}
            </div>
          </div>
          <div>
            <label className="label-tiny block mb-1">备注 (可选)</label>
            <input
              className="input-base text-sm"
              placeholder="转账截图编号 / 现金"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              data-testid="payment-notes"
            />
          </div>
        </div>

        <p className="text-[10px] text-slate-500 mb-4">
          ※ 记录为 {currentUser?.name} 于 {new Date().toLocaleString('zh-CN')} 确认收款
        </p>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">取消</button>
          <button
            onClick={submit}
            disabled={!valid || submitting}
            className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
            data-testid="confirm-mark-paid"
          >
            {submitting ? '处理中…' : (willBeFullyPaid ? '✓ 全额收款' : `✓ 部分收款 ${fmt(amt)}`)}
          </button>
        </div>
      </div>
    </div>
  );
}
